import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { collection, getCountFromServer, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_PACKAGES, formatPackageDuration, mergePackages } from "../utils/packages";

const formatAmount = (price) => Number(price).toLocaleString("en-US", {
  useGrouping: false,
  maximumFractionDigits: 2,
});

const getUssdCode = (price) => `*415*3582*${formatAmount(price)}#`;

export default function UssdHome({ migrationOnly = false }) {
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);
  const [ticketCounts, setTicketCounts] = useState({});
  const [loadingTicketCounts, setLoadingTicketCounts] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setTicketCounts({});
    setLoadingTicketCounts(true);

    const loadPackages = async () => {
      try {
        const snapshot = await getDocs(collection(db, "packages"));
        const savedPackages = snapshot.docs.map((packageDoc) => ({
          id: packageDoc.id,
          ...packageDoc.data(),
        }));
        const availablePackages = mergePackages(savedPackages)
          .filter((pkg) => pkg.portal === (migrationOnly ? "migration" : "standard"))
          .filter((pkg) => Number.isFinite(Number(pkg.price)) && Number(pkg.price) > 0)
          .sort((first, second) => Number(first.price) - Number(second.price));
        if (active) setPackages(availablePackages);

        const counts = await Promise.all(availablePackages.map(async (pkg) => {
          try {
            const availableTickets = query(
              collection(db, "credentials"),
              where("packageId", "==", pkg.id),
              where("used", "==", false)
            );
            const countSnapshot = await getCountFromServer(availableTickets);
            return [pkg.id, countSnapshot.data().count];
          } catch (countError) {
            console.error(`Ticket availability lookup failed for package ${pkg.id}:`, countError);
            return [pkg.id, null];
          }
        }));
        if (active) setTicketCounts(Object.fromEntries(counts));
      } catch (loadError) {
        console.error("Package loading error:", loadError);
        if (active) setError("We couldn’t load the latest package prices. Please refresh and try again.");
      } finally {
        if (active) {
          setLoadingPackages(false);
          setLoadingTicketCounts(false);
        }
      }
    };

    loadPackages();
    return () => {
      active = false;
    };
  }, [migrationOnly]);

  return (
    <main className="min-h-screen bg-[#f3f4f6] px-4 py-6 text-slate-800 sm:py-10">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8 flex items-center justify-between sm:mb-10">
          <Link to="/" className="text-xl font-extrabold tracking-tight text-slate-900">
            <span className="mr-2 text-blue-600">◉</span>Chidiz Wi-Fi
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/check-ticket"
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-100 sm:text-sm"
            >
              Get ticket
            </Link>
            <a href="tel:0243767677" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 sm:px-4 sm:text-sm">
              Help
            </a>
          </div>
        </header>

        <section className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Fast · Simple · Secure</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {migrationOnly ? "Choose your immigration package" : "Choose your internet package"}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
            Choose a package to open the payment code on your phone.
          </p>
        </section>

        {loadingPackages ? (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500 shadow-sm">
            Loading packages…
          </div>
        ) : error ? (
          <p role="alert" className="mt-8 rounded-xl border border-rose-200 bg-rose-50 p-4 text-center text-rose-700">
            {error}
          </p>
        ) : packages.length === 0 ? (
          <p className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-center text-slate-600 shadow-sm">
            No packages are available right now. Please contact support.
          </p>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-2 sm:mt-8 sm:gap-4">
            {packages.map((pkg, index) => {
              const code = getUssdCode(pkg.price);
              const devices = Number(pkg.devices || 1);
              const displayName = migrationOnly && pkg.id === "Immigration-32d"
                ? "Immigration"
                : pkg.name || formatPackageDuration(pkg) || "Wi-Fi package";
              const ticketCount = ticketCounts[pkg.id];
              return (
                <motion.article
                  key={pkg.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.28, delay: index * 0.06 }}
                  whileHover={{ y: -3 }}
                  className="flex min-h-0 flex-col items-center rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm transition-shadow hover:shadow-md sm:min-h-64 sm:rounded-2xl sm:p-6"
                >
                  <h2 className="text-base font-bold leading-tight text-slate-900 sm:text-2xl">{displayName}</h2>
                  {migrationOnly && formatPackageDuration(pkg) && (
                    <p className="mt-1 text-xs text-slate-500 sm:text-sm">{formatPackageDuration(pkg)}</p>
                  )}
                  <p className="mt-2 text-xl font-extrabold text-blue-700 sm:text-4xl">
                    GH₵ {formatAmount(pkg.price)}
                  </p>
                  <p className="mt-1 text-xs text-slate-600 sm:mt-2 sm:text-sm">
                    {devices > 1 ? `Up to ${devices} devices` : "1 device"}
                  </p>
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold sm:text-xs ${
                      loadingTicketCounts || ticketCount === null || ticketCount === undefined
                        ? "bg-slate-100 text-slate-600"
                        : ticketCount === 0
                          ? "bg-rose-50 text-rose-700"
                          : ticketCount <= 5
                            ? "bg-amber-50 text-amber-700"
                            : "bg-emerald-50 text-emerald-700"
                    }`}
                    aria-live="polite"
                  >
                    <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                      <path d="M3 4.75A1.75 1.75 0 0 1 4.75 3h10.5A1.75 1.75 0 0 1 17 4.75v1.086a2.75 2.75 0 0 0 0 5.328v1.086A1.75 1.75 0 0 1 15.25 14h-10.5A1.75 1.75 0 0 1 3 12.25v-1.086a2.75 2.75 0 0 0 0-5.328V4.75Zm6.25.5v1.5h1.5v-1.5h-1.5Zm0 3v1.5h1.5v-1.5h-1.5Zm0 3v1.5h1.5v-1.5h-1.5Z" />
                    </svg>
                    {loadingTicketCounts
                      ? "Checking tickets…"
                      : ticketCount === null || ticketCount === undefined
                        ? "Availability unavailable"
                        : `${ticketCount} ${ticketCount === 1 ? "ticket" : "tickets"} left`}
                  </div>
                  <a
                    href={`tel:${encodeURIComponent(code)}`}
                    aria-label={`Pay GHS ${formatAmount(pkg.price)} with USSD code ${code}`}
                    className="mt-3 flex min-h-10 w-full items-center justify-center rounded-lg border border-blue-800 bg-gradient-to-r from-blue-700 to-blue-500 px-2 py-1.5 text-center text-[10px] font-bold leading-4 text-white shadow-sm transition hover:from-blue-800 hover:to-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200 sm:mt-auto sm:min-h-11 sm:rounded-xl sm:px-3 sm:py-2 sm:text-sm sm:leading-5"
                  >
                    Pay now
                  </a>
                </motion.article>
              );
            })}
          </div>
        )}

        <section className="mx-auto mt-7 max-w-3xl text-center">
          <p className="text-sm font-medium leading-6 text-slate-600 sm:text-base">
            After paying, check your payment using the date, mobile money number, and amount shown on your receipt.
          </p>
          <Link
            to="/check-ticket"
            className="mt-4 inline-flex min-h-10 items-center justify-center rounded-lg border border-blue-800 bg-gradient-to-r from-blue-700 to-blue-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:from-blue-800 hover:to-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200"
          >
            I’ve paid — check payment and get ticket
          </Link>
          <p className="mt-4 text-xs leading-5 text-slate-500">
            Use the same phone number you paid from and enter the exact package amount.
          </p>
        </section>
      </div>
    </main>
  );
}
